/**
 * Clip Export Utility
 * Handles MP3 clip generation with Range requests and size guards
 * Fixes: Bugs #10, #11, #12, #14
 */

import lamejs from 'lamejs';
import MPEGMode from 'lamejs/src/js/MPEGMode.js';
import Lame from 'lamejs/src/js/Lame.js';
import BitStream from 'lamejs/src/js/BitStream.js';
import { MAX_SAFE_DECODE_BYTES, MAX_CLIP_SECONDS } from '@constants/config';

// Global polyfills for lamejs internal modules (Bug #10, #14)
globalThis.MPEGMode = MPEGMode;
globalThis.Lame = Lame;
globalThis.BitStream = BitStream;

/**
 * Probe for Range request support (Bug #12, #14 fix)
 * Uses GET with Range: bytes=0-1023 (1KB probe)
 */
async function probeRangeSupport(url) {
  try {
    const response = await fetch(url, {
      headers: { Range: 'bytes=0-1023' }
    });

    // Check if server supports Range requests
    if (response.status === 206) {
      // Parse Content-Range header to get file size
      const contentRange = response.headers.get('Content-Range');
      if (contentRange) {
        // Format: "bytes 0-1023/TOTAL_SIZE"
        const match = contentRange.match(/bytes \d+-\d+\/(\d+)/);
        if (match) {
          const fileSize = parseInt(match[1], 10);
          return { supported: true, fileSize };
        }
      }
      return { supported: true, fileSize: 0 };
    }

    // Server doesn't support Range requests
    return { supported: false, fileSize: 0 };
  } catch (error) {
    console.warn('Range probe failed:', error);
    return { supported: false, fileSize: 0 };
  }
}

/**
 * Download and export MP3 clip
 */
export async function downloadAudioClip({
  episodeUrl,
  episodeTitle,
  startTime,
  endTime,
  episodeDuration,
  onProgress
}) {
  // Validate inputs
  if (!episodeUrl || startTime < 0 || endTime <= startTime) {
    throw new Error('Invalid clip parameters');
  }

  const clipDuration = endTime - startTime;

  // Probe for Range support
  onProgress?.(0, 'Preparing...');
  const { supported: rangeSupported, fileSize } = await probeRangeSupport(episodeUrl);

  let useRange = rangeSupported && fileSize > 0 && episodeDuration > 0;
  let rangeStart = 0;
  let rangeEnd = 0;

  if (useRange) {
    // Calculate byte range (assume 128 kbps bitrate)
    const bytesPerSecond = fileSize / episodeDuration;
    rangeStart = Math.max(0, Math.floor(startTime * bytesPerSecond) - 16384); // 16KB padding
    rangeEnd = Math.min(fileSize - 1, Math.ceil(endTime * bytesPerSecond) + 16384);

    console.log(`Using Range request: bytes ${rangeStart}-${rangeEnd} of ${fileSize}`);
  }

  // Fetch audio
  onProgress?.(5, 'Downloading audio...');

  const fetchOptions = {};
  if (useRange) {
    fetchOptions.headers = { Range: `bytes=${rangeStart}-${rangeEnd}` };
  }

  const response = await fetch(episodeUrl, fetchOptions);

  // Check if Range was honored
  if (useRange && response.status === 200) {
    console.warn('Server ignored Range header, downloading full file');
    useRange = false;
    rangeStart = 0;
  }

  // Pre-stream size guard (Bug #11, #14 fix)
  const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);

  if (contentLength > MAX_SAFE_DECODE_BYTES && !useRange) {
    // Calculate clip size
    const estimatedClipBytes = clipDuration * 16000; // 128 kbps = 16 KB/s

    if (estimatedClipBytes <= MAX_SAFE_DECODE_BYTES) {
      // Small clip from large episode - allow it
      console.warn(`Allowing full-file fetch for small clip (${Math.round(clipDuration / 60)} min)`);
    } else {
      // Clip is too large
      const clipMinutes = Math.round(clipDuration / 60);
      throw new Error(
        `This clip (${clipMinutes} minutes) is too long when the server doesn't support partial downloads. Choose under 20 minutes.`
      );
    }
  }

  // Stream response with progress
  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    received += value.length;

    // In-stream size guard (Bug #14 fix)
    if (received > MAX_SAFE_DECODE_BYTES && !useRange) {
      const estimatedClipBytes = clipDuration * 16000;

      if (estimatedClipBytes <= MAX_SAFE_DECODE_BYTES) {
        // Small clip - continue
      } else {
        reader.cancel();
        throw new Error('Download exceeded safe processing limit.');
      }
    }

    // Update progress (5-50%)
    const progress = Math.min(50, 5 + (received / (contentLength || received)) * 45);
    onProgress?.(progress, 'Downloading audio...');
  }

  // Concatenate chunks
  const audioData = new Uint8Array(received);
  let position = 0;
  for (const chunk of chunks) {
    audioData.set(chunk, position);
    position += chunk.length;
  }

  // Decode MP3
  onProgress?.(55, 'Decoding audio...');
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const audioBuffer = await audioContext.decodeAudioData(audioData.buffer);

  // Adjust times if Range was used
  let actualStartTime = startTime;
  let actualEndTime = endTime;

  if (useRange && rangeStart > 0) {
    const rangeStartTime = rangeStart / (fileSize / episodeDuration);
    actualStartTime = Math.max(0, startTime - rangeStartTime);
    actualEndTime = endTime - rangeStartTime;
  }

  // Extract clip portion
  const sampleRate = audioBuffer.sampleRate;
  const startSample = Math.floor(actualStartTime * sampleRate);
  const endSample = Math.floor(actualEndTime * sampleRate);
  const clipLength = endSample - startSample;

  const leftChannel = audioBuffer.getChannelData(0).slice(startSample, endSample);
  const rightChannel = audioBuffer.numberOfChannels > 1
    ? audioBuffer.getChannelData(1).slice(startSample, endSample)
    : leftChannel;

  // Encode to MP3
  onProgress?.(70, 'Encoding MP3...');
  const mp3Encoder = new lamejs.Mp3Encoder(2, sampleRate, 128);
  const mp3Data = [];
  const samplesPerFrame = 1152;

  for (let i = 0; i < clipLength; i += samplesPerFrame) {
    const leftChunk = convertFloat32ToInt16(leftChannel.subarray(i, i + samplesPerFrame));
    const rightChunk = convertFloat32ToInt16(rightChannel.subarray(i, i + samplesPerFrame));
    const mp3buf = mp3Encoder.encodeBuffer(leftChunk, rightChunk);

    if (mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }

    // Update progress (70-95%)
    const progress = 70 + (i / clipLength) * 25;
    onProgress?.(progress, 'Encoding MP3...');
  }

  // Flush encoder
  const mp3buf = mp3Encoder.flush();
  if (mp3buf.length > 0) {
    mp3Data.push(mp3buf);
  }

  // Create Blob and download
  onProgress?.(98, 'Finalizing...');
  const blob = new Blob(mp3Data, { type: 'audio/mp3' });

  // Generate filename
  const safeTitle = episodeTitle.replace(/[^a-zA-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
  const startMin = Math.floor(startTime / 60);
  const startSec = Math.floor(startTime % 60);
  const endMin = Math.floor(endTime / 60);
  const endSec = Math.floor(endTime % 60);
  const filename = `${safeTitle}_${startMin}m${startSec}s_${endMin}m${endSec}s.mp3`;

  // Trigger download
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  onProgress?.(100, 'Complete!');
}

/**
 * Convert Float32Array to Int16Array
 */
function convertFloat32ToInt16(buffer) {
  const l = buffer.length;
  const buf = new Int16Array(l);
  for (let i = 0; i < l; i++) {
    const s = Math.max(-1, Math.min(1, buffer[i]));
    buf[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return buf;
}
